import React from 'react';
import { Form, Input, DatePicker } from 'antd';
import { UserOutlined, MailOutlined, PhoneOutlined, IdcardOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useSignUp } from '../context/SignUpContext';
import StepNavigation from '../components/StepNavigation';
import { validCPF } from '../../../../utils/cpfValidate';

const StepPersonalInfo = () => {
  const [form] = Form.useForm();
  const { formData, updatePerson, nextStep, prevStep } = useSignUp();

  // Format CPF as user types
  const formatCPF = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `${numbers.slice(0, 3)}.${numbers.slice(3)}`;
    if (numbers.length <= 9) return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`;
    return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9, 11)}`;
  };

  // Format phone as user types
  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    if (numbers.length <= 11) return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
  };

  const handleCPFChange = (e) => {
    const formatted = formatCPF(e.target.value);
    form.setFieldValue('taxpayer_id', formatted);
    updatePerson('taxpayer_id', formatted);
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    form.setFieldValue('phone', formatted);
    updatePerson('phone', '+55' + formatted.replace(/\D/g, ''));
  };

  const handleNext = async () => {
    try {
      await form.validateFields();
      nextStep();
    } catch (err) {
      // Validation failed
    }
  };

  // Calculate minimum date (18 years ago)
  const minDate = dayjs().subtract(18, 'year');

  return (
    <div className="step-content">
      <div className="step-header">
        <h2>Dados pessoais</h2>
        <p>Informe os dados do responsável pela conta.</p>
      </div>

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          first_name: formData.person.first_name,
          last_name: formData.person.last_name,
          email: formData.person.email,
          phone: formData.person.phone ? formatPhone(formData.person.phone.replace('+55', '')) : '',
          taxpayer_id: formData.person.taxpayer_id ? formatCPF(formData.person.taxpayer_id) : '',
          birthdate: formData.person.birthdate ? dayjs(formData.person.birthdate) : null,
          mother_name: formData.person.mother_name,
        }}
        requiredMark={false}
      >
        <div className="form-row">
          <Form.Item
            name="first_name"
            label="Nome"
            rules={[{ required: true, message: 'Digite seu nome' }]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="Seu nome"
              size="large"
              onChange={(e) => updatePerson('first_name', e.target.value)}
            />
          </Form.Item>

          <Form.Item
            name="last_name"
            label="Sobrenome"
            rules={[{ required: true, message: 'Digite seu sobrenome' }]}
          >
            <Input
              placeholder="Seu sobrenome"
              size="large"
              onChange={(e) => updatePerson('last_name', e.target.value)}
            />
          </Form.Item>
        </div>

        <Form.Item
          name="email"
          label="E-mail de contato"
          rules={[
            { required: true, message: 'Digite seu e-mail' },
            { type: 'email', message: 'Digite um e-mail válido' },
          ]}
        >
          <Input
            prefix={<MailOutlined />}
            placeholder="seu@email.com"
            size="large"
            onChange={(e) => updatePerson('email', e.target.value)}
          />
        </Form.Item>

        <Form.Item
          name="phone"
          label="Telefone"
          rules={[
            { required: true, message: 'Digite seu telefone' },
            {
              validator: (_, value) => {
                const numbers = value?.replace(/\D/g, '') || '';
                if (numbers.length < 10) {
                  return Promise.reject('Telefone inválido');
                }
                return Promise.resolve();
              }
            }
          ]}
        >
          <Input
            prefix={<PhoneOutlined />}
            placeholder="(11) 99999-9999"
            size="large"
            onChange={handlePhoneChange}
            maxLength={15}
          />
        </Form.Item>

        <Form.Item
          name="taxpayer_id"
          label="CPF"
          rules={[
            { required: true, message: 'Digite seu CPF' },
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                const numbers = value.replace(/\D/g, '');
                if (!validCPF(numbers)) {
                  return Promise.reject('CPF inválido');
                }
                return Promise.resolve();
              }
            }
          ]}
        >
          <Input
            prefix={<IdcardOutlined />}
            placeholder="000.000.000-00"
            size="large"
            onChange={handleCPFChange}
            maxLength={14}
          />
        </Form.Item>

        <Form.Item
          name="birthdate"
          label="Data de nascimento"
          rules={[
            { required: true, message: 'Selecione sua data de nascimento' },
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                if (value.isAfter(minDate)) {
                  return Promise.reject('Você deve ter pelo menos 18 anos');
                }
                return Promise.resolve();
              }
            }
          ]}
        >
          <DatePicker
            placeholder="DD/MM/AAAA"
            size="large"
            format={{ format: 'DD/MM/YYYY', type: 'mask' }}
            style={{ width: '100%' }}
            disabledDate={(current) => current && current > minDate}
            onChange={(date) => updatePerson('birthdate', date ? date.format('YYYY-MM-DD') : '')}
          />
        </Form.Item>

        <Form.Item
          name="mother_name"
          label="Nome da mãe"
          rules={[{ required: true, message: 'Digite o nome da sua mãe' }]}
        >
          <Input
            prefix={<UserOutlined />}
            placeholder="Nome completo da mãe"
            size="large"
            onChange={(e) => updatePerson('mother_name', e.target.value)}
          />
        </Form.Item>
      </Form>

      <StepNavigation
        onNext={handleNext}
        onBack={prevStep}
      />
    </div>
  );
};

export default StepPersonalInfo;
